import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { Card, Button, Select } from '../components/ui';
import { TemplateSelector } from '../components/generate/TemplateSelector';
import { SlotAsset } from '../components/generate/AssetSlotMapper';
import { ImageSlotPreview, VideoSlotPreview } from '../components/generate/AssetSlotMapper';
import { SceneGroupedSlots } from '../components/generate/SceneGroupedSlots';
import { GenerationProgress } from '../components/generate/GenerationProgress';
import { RunList } from '../components/generate/RunList';
import { DraftWarningModal } from '../components/generate/DraftWarningModal';
import { AssetGallery } from '../components/assets/AssetGallery';
import { AssetUploadModal } from '../components/assets/AssetUploadModal';
import TemplateReferencePreview from '../components/generate/TemplateReferencePreview';
import { useTemplates, TemplateSlot, SlotTrimConfig, SlotReferencePreview, Template } from '../hooks/useTemplates';
import { useAssets } from '../hooks/useAssets';
import { useAssetUpload } from '../hooks/useAssetUpload';
import { useJobCreate } from '../hooks/useJobCreate';
import { useJobStatus } from '../hooks/useJobStatus';
import { useRuns } from '../hooks/useRuns';
import { useDraft, DraftSlotAsset } from '../hooks/useDraft';
import { RenderProps } from '../lib/slotProps';
import { MARKETS } from '../lib/markets';
import { getJobDisplayTitle } from '../lib/jobDisplayTitle';
import { getVisibleCharCount } from '../lib/textUtils';
import { getMarketDefaultVariant } from '../lib/dreamCatEffects';
import { getResolvedEffectSelection, getDefaultEffectSelection } from '../lib/effectGroups';
import { getOpeningVideoCount, getOpeningSegments, OPENING_FPS, isOpeningVideoSlot, isSlotRequired, isSlotVisible } from '../lib/openingVideoSlots';
import { useUnsavedChangesGuard } from '../contexts/UnsavedChangesGuardContext';
import { Video, ArrowLeft, FileEdit } from 'lucide-react';

interface FixedDurationTrimConfig {
  durationFrames: number;
  fps: number;
  loopPreview: boolean;
}

const TRIM_EPSILON = 0.0001;

function getFixedDurationTrimConfig(trim: SlotTrimConfig | undefined): FixedDurationTrimConfig | null {
  if (!trim || trim.enabled !== true || trim.mode !== 'fixed-duration') return null;
  const durationFrames = trim.duration_frames;
  const fps = trim.fps;
  if (typeof durationFrames !== 'number' || durationFrames <= 0) return null;
  if (typeof fps !== 'number' || fps <= 0) return null;
  return {
    durationFrames,
    fps,
    loopPreview: trim.loop_preview !== false,
  };
}

function getDefaultSelectValueForSlot(slot: TemplateSlot, template: Template, market: string): string {
  if (slot.slotId === 'dropCardVariant') {
    if (template.effectGroups) {
      const defaultSelection = getDefaultEffectSelection(template.effectGroups, market);
      if (defaultSelection) return defaultSelection.option.id;
    }
    return getMarketDefaultVariant(market);
  }
  return slot.defaultValue ?? '';
}

function GeneratePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { templates, loading: templatesLoading, error: templatesError } = useTemplates();
  const {
    assets,
    loading: assetsLoading,
    loadingMore: assetsLoadingMore,
    hasMore: assetsHasMore,
    totalCount: assetsTotalCount,
    uploadAsset,
    loadMore: loadMoreAssets,
  } = useAssets();
  const { uploadMultiple, uploading, uploadAsset: uploadSlotFile } = useAssetUpload();
  const { createJob, creating, error: createJobError } = useJobCreate();
  const { saveDraft, deleteDraft, consumeDraft, getDraft, saving: savingDraft } = useDraft();
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const { jobStatus } = useJobStatus(currentJobId);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<string>('global');
  const [slotAssets, setSlotAssets] = useState<SlotAsset[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewVideoError, setPreviewVideoError] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  /** Version locked at the moment the operator selected the template. Used for mismatch detection. */
  const [lockedTemplateVersion, setLockedTemplateVersion] = useState<string | null>(null);
  /** Whether the operator has clicked "New Run" and is currently filling a run. */
  const [inNewRun, setInNewRun] = useState(false);
  /** ID of the draft currently being edited. Null when starting a fresh run. */
  const [resumingDraftId, setResumingDraftId] = useState<string | null>(null);
  /** Draft warning modal visibility. */
  const [showDraftModal, setShowDraftModal] = useState(false);
  /** True while local files are being uploaded as part of a Save Draft operation. */
  const [uploadingForDraft, setUploadingForDraft] = useState(false);
  /** Error message from the most recent Save Draft attempt (upload or Firestore failure). */
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const { registerHandler } = useUnsavedChangesGuard();
  /** Action to execute once the user confirms/dismisses the draft warning modal. */
  const pendingContinuationRef = useRef<(() => void) | null>(null);
  /** True after a successful Generate submission — suppresses the dirty warning. */
  const hasSubmittedRef = useRef(false);
  /** Ref to suppress the apply-query-param effect from re-running. */
  const queryParamApplied = useRef(false);
  /** Tracks whether the operator has manually selected a dropCardVariant in this run. */
  const dropCardVariantIsManual = useRef(false);

  // Runs for the selected template + market (most-recent first, client-side market filter).
  const { runs, loading: runsLoading } = useRuns(selectedTemplateId, selectedMarket);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const getSlotTrimConfig = (slotId: string): FixedDurationTrimConfig | null => {
    const slot = selectedTemplate?.slots.find((s) => s.slotId === slotId);
    return slot?.type === 'video' ? getFixedDurationTrimConfig(slot.trim) : null;
  };

  const getSlotAssetSourceKey = (slotAsset: SlotAsset): string => {
    if (slotAsset.file) {
      return `file:${slotAsset.file.name}:${slotAsset.file.size}:${slotAsset.file.lastModified}`;
    }
    if (slotAsset.storageUrl) {
      return `url:${slotAsset.storageUrl}`;
    }
    return 'none';
  };

  // ── Dirty-state detection ───────────────────────────────────────────────────
  // A form is "dirty" if the operator is in New Run mode and has added content
  // to at least one slot beyond the initial default values.
  const isDirty = inNewRun && !hasSubmittedRef.current && slotAssets.some((sa) => {
    const slot = selectedTemplate?.slots.find((s) => s.slotId === sa.slotId);
    if (!slot) return false;
    if (slot.type === 'image' || slot.type === 'video' || slot.type === 'audio') {
      return sa.file !== null || !!sa.storageUrl;
    }
    if (slot.type === 'text') {
      return !!sa.textValue && sa.textValue.trim().length > 0;
    }
    if (slot.type === 'select' || slot.type === 'color') {
      const defaultValue = selectedTemplate
        ? getDefaultSelectValueForSlot(slot, selectedTemplate, selectedMarket)
        : (slot.defaultValue ?? '');
      return sa.textValue !== defaultValue;
    }
    return false;
  });

  // ── Browser unload warning ──────────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        // Modern browsers show their own message; setting returnValue is required
        // for older browser compatibility.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // ── Draft modal handlers ────────────────────────────────────────────────────

  /**
   * Upload any slot that has a local File but no storageUrl yet.
   * Returns a new slotAssets array where uploaded slots have file=null and
   * storageUrl set to the Firebase Storage download URL.
   * Throws if any upload fails so the caller can abort the draft save.
   */
  const uploadLocalFilesForDraft = useCallback(
    async (currentSlots: SlotAsset[]): Promise<SlotAsset[]> => {
      const projectId = resumingDraftId ? `draft_${resumingDraftId}` : `draft_${Date.now()}`;
      const updated = [...currentSlots];
      for (let i = 0; i < updated.length; i++) {
        const sa = updated[i];
        if (sa.file && !sa.storageUrl) {
          console.log(`[GeneratePage] uploading local file for draft — slot="${sa.slotId}" file="${sa.file.name}"`);
          const storageUrl = await uploadSlotFile(sa.file, sa.slotId, projectId);
          // Replace the local File with the durable storageUrl so the slot
          // behaves like a gallery asset from this point on.
          updated[i] = { ...sa, file: null, storageUrl };
        }
      }
      return updated;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resumingDraftId, uploadSlotFile],
  );

  const handleModalSaveDraft = useCallback(async () => {
    if (!selectedTemplate) return;
    setDraftSaveError(null);
    setUploadingForDraft(true);
    try {
      const resolvedSlots = await uploadLocalFilesForDraft(slotAssets);
      setSlotAssets(resolvedSlots);
      const draftId = await saveDraft(resolvedSlots, selectedTemplate, selectedMarket, resumingDraftId);
      setResumingDraftId(draftId);
      setShowDraftModal(false);
      const continuation = pendingContinuationRef.current;
      pendingContinuationRef.current = null;
      continuation?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      setDraftSaveError(msg);
      console.error('[GeneratePage] saveDraft failed', err);
    } finally {
      setUploadingForDraft(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, slotAssets, selectedMarket, resumingDraftId, saveDraft, uploadLocalFilesForDraft]);

  const handleModalDiscard = useCallback(() => {
    setShowDraftModal(false);
    const continuation = pendingContinuationRef.current;
    pendingContinuationRef.current = null;
    continuation?.();
  }, []);

  const handleModalCancel = useCallback(() => {
    setShowDraftModal(false);
    pendingContinuationRef.current = null;
  }, []);

  const openDraftModalWithContinuation = useCallback((continuation: () => void) => {
    pendingContinuationRef.current = continuation;
    setDraftSaveError(null);
    setShowDraftModal(true);
  }, []);

  // ── Guard helper: run an action, showing the draft modal if form is dirty ──
  const guardedAction = useCallback((action: () => void) => {
    if (isDirty) {
      openDraftModalWithContinuation(action);
    } else {
      action();
    }
  }, [isDirty, openDraftModalWithContinuation]);

  useEffect(() => {
    if (inNewRun && isDirty && !hasSubmittedRef.current) {
      registerHandler({
        isDirty: true,
        onAttemptNavigate: openDraftModalWithContinuation,
      });
      return () => registerHandler(null);
    }
    registerHandler(null);
    return () => registerHandler(null);
  }, [inNewRun, isDirty, registerHandler, openDraftModalWithContinuation]);

  // ── Manual Save Draft ───────────────────────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    if (!selectedTemplate) return;
    setDraftSaveError(null);
    setUploadingForDraft(true);
    try {
      const resolvedSlots = await uploadLocalFilesForDraft(slotAssets);
      setSlotAssets(resolvedSlots);
      const draftId = await saveDraft(resolvedSlots, selectedTemplate, selectedMarket, resumingDraftId);
      setResumingDraftId(draftId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      setDraftSaveError(msg);
      console.error('[GeneratePage] manual saveDraft failed', err);
    } finally {
      setUploadingForDraft(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, slotAssets, selectedMarket, resumingDraftId, saveDraft, uploadLocalFilesForDraft]);

  // ── Resume Draft ─────────────────────────────────────────────────────────────
  const buildSlotAssetsFromDraft = useCallback(
    (template: NonNullable<typeof selectedTemplate>, draftSlots: DraftSlotAsset[], market: string): SlotAsset[] => {
      return template.slots.map((slot) => {
        const saved = draftSlots.find((d) => d.slotId === slot.slotId);
        const defaultSelectValue = getDefaultSelectValueForSlot(slot, template, market);
        const resolvedDropCardValue =
          slot.slotId === 'dropCardVariant' && template.effectGroups
            ? (getResolvedEffectSelection(template.effectGroups, saved?.textValue ?? null, market)?.option.id ?? defaultSelectValue)
            : defaultSelectValue;
        const savedTextValue =
          slot.slotId === 'dropCardVariant' && template.effectGroups
            ? (getResolvedEffectSelection(template.effectGroups, saved?.textValue ?? null, market)?.option.id ?? null)
            : (saved?.textValue ?? null);
        return {
          slotId: slot.slotId,
          file: null,
          textValue: savedTextValue ??
            (slot.type === 'select'
              ? resolvedDropCardValue
              : null),
          cropAnchor: saved?.cropAnchor ?? slot.specs?.crop_anchor ?? 'center',
          trimStartSeconds:
            typeof saved?.trimStartSeconds === 'number'
              ? saved.trimStartSeconds
              : getFixedDurationTrimConfig(slot.trim)
              ? 0
              : undefined,
          videoDurationSeconds: saved?.videoDurationSeconds ?? undefined,
          storageUrl: saved?.storageUrl ?? undefined,
          renderUrl: saved?.renderUrl ?? undefined,
          assetName: saved?.assetName ?? undefined,
          assetSize: saved?.assetSize ?? undefined,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResumeDraft = useCallback(
    (draftId: string, draftSlots: DraftSlotAsset[]) => {
      if (!selectedTemplate) return;
      const restored = buildSlotAssetsFromDraft(selectedTemplate, draftSlots, selectedMarket);
      setSlotAssets(restored);
      setResumingDraftId(draftId);
      setSelectedSlotId(null);
      dropCardVariantIsManual.current = false;
      setInNewRun(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTemplate, selectedMarket, buildSlotAssetsFromDraft],
  );

  const handleDeleteDraft = useCallback(
    async (draftId: string) => {
      await deleteDraft(draftId);
      if (resumingDraftId === draftId) {
        setResumingDraftId(null);
      }
    },
    [deleteDraft, resumingDraftId],
  );

  // Apply ?templateId=<id> and ?draftId=<id> query params once templates have loaded.
  useEffect(() => {
    if (templatesLoading || queryParamApplied.current) return;
    const paramId = searchParams.get('templateId');
    const draftId = searchParams.get('draftId');
    if (paramId && templates.some((t) => t.id === paramId)) {
      setSelectedTemplateId(paramId);
      queryParamApplied.current = true;
      // If a draftId is also present, load and restore the draft once the template
      // is available. We defer to the next effect cycle by storing the draftId.
      if (draftId) {
        (async () => {
          try {
            const draft = await getDraft(draftId);
            if (draft) {
              // Wait for the template to be set before restoring — the selectedTemplate
              // will be derived from state on the next render.
              const tmpl = templates.find((t) => t.id === paramId);
              if (tmpl) {
                const market = draft.market;
                setSelectedMarket(market);
                const restored = buildSlotAssetsFromDraft(tmpl, draft.slotDraft, market);
                setSlotAssets(restored);
                setResumingDraftId(draftId);
                setInNewRun(true);
              }
            }
          } catch (err) {
            console.error('[GeneratePage] failed to load draft from URL param', err);
          }
        })();
      }
    } else if (paramId) {
      queryParamApplied.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // searchParams is intentionally omitted: we read the initial URL param once;
  // setSelectedTemplateId is a stable setter that never changes.
  }, [templatesLoading, templates]);

  // Initialize slot assets when a new template is selected (keyed on ID, not the full object,
  // so we don't re-run when the Firestore document updates in-place while the operator is
  // filling slots — e.g. when only template_version changes).
  useEffect(() => {
    if (selectedTemplate) {
      dropCardVariantIsManual.current = false;
      setSlotAssets(
        selectedTemplate.slots.map((slot) => ({
          slotId: slot.slotId,
          file: null,
          textValue: slot.type === 'select'
            ? getDefaultSelectValueForSlot(slot, selectedTemplate, selectedMarket)
            : null,
          cropAnchor: slot.specs?.crop_anchor ?? 'center',
          trimStartSeconds: getFixedDurationTrimConfig(slot.trim) ? 0 : undefined,
          videoDurationSeconds: undefined,
        }))
      );
      setPreviewVideoError(false);
      setSelectedSlotId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId]);

  // Lock template_version at the moment the operator opens a template.
  // Intentionally re-runs only on ID change — not on version change — because
  // a version change while the operator has the form open is exactly the
  // mismatch scenario we want to surface (templateVersionMismatch below).
  useEffect(() => {
    if (selectedTemplate) {
      setLockedTemplateVersion(selectedTemplate.template_version);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId]);

  const handleAssetAdded = (slotId: string, file: File) => {
    setSlotAssets((prev) =>
      prev.map((sa) =>
        sa.slotId === slotId
          ? (() => {
              const trimConfig = getSlotTrimConfig(slotId);
              const previousSourceKey = getSlotAssetSourceKey(sa);
              const nextSourceKey = `file:${file.name}:${file.size}:${file.lastModified}`;
              const keepTrimSelection =
                trimConfig && previousSourceKey === nextSourceKey;
              return {
                ...sa,
                file,
                renderUrl: undefined,
                storageUrl: undefined,
                assetName: undefined,
                assetSize: undefined,
                trimStartSeconds: trimConfig
                  ? (keepTrimSelection ? sa.trimStartSeconds ?? 0 : 0)
                  : undefined,
                videoDurationSeconds: keepTrimSelection ? sa.videoDurationSeconds : undefined,
              };
            })()
          : sa
      )
    );
  };

  const handleGalleryAssetAdded = (slotId: string, storageUrl: string, name: string, size: number, renderUrl?: string) => {
    setSlotAssets((prev) =>
      prev.map((sa) =>
        sa.slotId === slotId
          ? (() => {
              const trimConfig = getSlotTrimConfig(slotId);
              const previousSourceKey = getSlotAssetSourceKey(sa);
              const nextSourceKey = `url:${storageUrl}`;
              const keepTrimSelection =
                trimConfig && previousSourceKey === nextSourceKey;
              return {
                ...sa,
                file: null,
                storageUrl,
                renderUrl,
                assetName: name,
                assetSize: size,
                trimStartSeconds: trimConfig
                  ? (keepTrimSelection ? sa.trimStartSeconds ?? 0 : 0)
                  : undefined,
                videoDurationSeconds: keepTrimSelection ? sa.videoDurationSeconds : undefined,
              };
            })()
          : sa
      )
    );
  };

  const handleAssetRemoved = (slotId: string) => {
    setSlotAssets((prev) =>
      prev.map((sa) =>
        sa.slotId === slotId
          ? {
              ...sa,
              file: null,
              renderUrl: undefined,
              storageUrl: undefined,
              assetName: undefined,
              assetSize: undefined,
              trimStartSeconds: getSlotTrimConfig(slotId) ? 0 : undefined,
              videoDurationSeconds: undefined,
            }
          : sa
      )
    );
  };

  const handleTextChanged = (slotId: string, value: string) => {
    if (slotId === 'dropCardVariant') {
      dropCardVariantIsManual.current = true;
    }
    setSlotAssets((prev) =>
      prev.map((sa) => (sa.slotId === slotId ? { ...sa, textValue: value } : sa))
    );
  };

  const handleCropAnchorChanged = (slotId: string, anchor: string) => {
    setSlotAssets((prev) =>
      prev.map((sa) => (sa.slotId === slotId ? { ...sa, cropAnchor: anchor } : sa))
    );
  };

  const handleTrimStartChanged = (slotId: string, trimStartSeconds: number) => {
    setSlotAssets((prev) =>
      prev.map((sa) => {
        if (sa.slotId !== slotId) return sa;
        const trimConfig = getSlotTrimConfig(slotId);
        if (!trimConfig) return sa;
        const requiredDurationSeconds = trimConfig.durationFrames / trimConfig.fps;
        const maxStart =
          typeof sa.videoDurationSeconds === 'number'
            ? Math.max(0, sa.videoDurationSeconds - requiredDurationSeconds)
            : Number.POSITIVE_INFINITY;
        const clamped = Math.min(Math.max(0, trimStartSeconds), maxStart);
        return { ...sa, trimStartSeconds: clamped };
      }),
    );
  };

  const handleVideoDurationDetected = (slotId: string, durationSeconds: number) => {
    setSlotAssets((prev) =>
      prev.map((sa) => {
        if (sa.slotId !== slotId) return sa;
        const trimConfig = getSlotTrimConfig(slotId);
        if (!trimConfig) return { ...sa, videoDurationSeconds: durationSeconds };
        const requiredDurationSeconds = trimConfig.durationFrames / trimConfig.fps;
        const maxStart = Math.max(0, durationSeconds - requiredDurationSeconds);
        const nextTrimStart = Math.min(Math.max(0, sa.trimStartSeconds ?? 0), maxStart);
        return {
          ...sa,
          videoDurationSeconds: durationSeconds,
          trimStartSeconds: nextTrimStart,
        };
      }),
    );
  };

  // Clear uploaded assets after a successful render, keeping the template selected for the next run.
  useEffect(() => {
    if (jobStatus?.status === 'completed') {
      setSlotAssets((prev) =>
        prev.map((sa) => {
          const trimConfig = getSlotTrimConfig(sa.slotId);
          return {
            ...sa,
            file: null,
            textValue: null,
            renderUrl: undefined,
            storageUrl: undefined,
            assetName: undefined,
            assetSize: undefined,
            trimStartSeconds: trimConfig ? 0 : undefined,
            videoDurationSeconds: undefined,
          };
        })
      );
      setSelectedSlotId(null);
      setCurrentJobId(null);
      // Return operator to the run list after a successful render.
      setInNewRun(false);
    }
  }, [jobStatus?.status]);

  const doReset = () => {
    setSelectedTemplateId(null);
    setSlotAssets([]);
    setCurrentJobId(null);
    setDownloadError(null);
    setLockedTemplateVersion(null);
    setResumingDraftId(null);
    hasSubmittedRef.current = false;
    setInNewRun(false);
  };

  const handleReset = () => guardedAction(doReset);

  const doBackToRunList = () => {
    setCurrentJobId(null);
    setDownloadError(null);
    dropCardVariantIsManual.current = false;
    setSlotAssets(
      selectedTemplate
        ? selectedTemplate.slots.map((slot) => ({
            slotId: slot.slotId,
            file: null,
            textValue: slot.type === 'select'
              ? getDefaultSelectValueForSlot(slot, selectedTemplate, selectedMarket)
              : null,
            cropAnchor: slot.specs?.crop_anchor ?? 'center',
            trimStartSeconds: getFixedDurationTrimConfig(slot.trim) ? 0 : undefined,
            videoDurationSeconds: undefined,
          }))
        : [],
    );
    setSelectedSlotId(null);
    setResumingDraftId(null);
    hasSubmittedRef.current = false;
    setInNewRun(false);
  };

  const handleBackToRunList = () => guardedAction(doBackToRunList);

  const handleGenerateVideo = async () => {
    if (!selectedTemplate) return;
    setDownloadError(null);

    // Clear the Slot Preview panel immediately on click
    setSelectedSlotId(null);

    try {
      // Generate a project ID
      const projectId = `project_${Date.now()}`;

      // Upload file assets (images/videos) — skip slots that already have a storageUrl
      // (i.e. assets dropped from the gallery that are already in Firebase Storage).
      const assetsToUpload = slotAssets
        .filter((sa) => {
          if (sa.file === null || sa.storageUrl) return false;
          const slot = selectedTemplate.slots.find((s) => s.slotId === sa.slotId);
          return slot ? isRuntimeSlotVisible(slot) : false;
        })
        .map((sa) => ({
          file: sa.file!,
          slotId: sa.slotId,
          uploaded: false,
        }));

      const uploadedAssets = await uploadMultiple(assetsToUpload, projectId);

      // Build new structured props keyed by slot_id.
      // End Frame color overrides are included as plain strings alongside SlotProp values.
      const props: RenderProps = {};
      slotAssets.forEach((slotAsset) => {
        const slot = selectedTemplate.slots.find((s) => s.slotId === slotAsset.slotId);
        if (!slot) return;
        const slotVisible = isRuntimeSlotVisible(slot);

        if (!slotVisible && (slot.type === 'image' || slot.type === 'video' || slot.type === 'audio')) {
          props[slotAsset.slotId] = null;
          return;
        }

        if (slot.type === 'text') {
          props[slotAsset.slotId] = {
            type: 'text',
            value: slotAsset.textValue ?? '',
            // Omit language when null/undefined — Firestore SDK v10 throws on undefined field values.
            ...(slot.specs?.language != null ? { language: slot.specs.language } : {}),
          };
        } else if (slot.type === 'select') {
          if (slot.slotId === 'dropCardVariant' && selectedTemplate.effectGroups) {
            const selectedOption = getResolvedEffectSelection(
              selectedTemplate.effectGroups,
              slotAsset.textValue ?? null,
            )?.option;
            props[slotAsset.slotId] =
              selectedOption?.value
              ?? slotAsset.textValue
              ?? slot.defaultValue
              ?? '';
          } else {
            props[slotAsset.slotId] = slotAsset.textValue ?? slot.defaultValue ?? '';
          }
        } else if (slot.type === 'color') {
          // Color slots are passed as hex string values
          props[slotAsset.slotId] = slotAsset.textValue ?? slot.specs?.default ?? '#000000';
        } else {
          // Gallery-dropped assets already have a storageUrl; uploaded files get one after upload.
          const assetStorageUrl =
            slotAsset.storageUrl ??
            uploadedAssets.find((ua) => ua.slotId === slotAsset.slotId)?.storageUrl;

          if (assetStorageUrl) {
            if (slot.type === 'audio') {
              props[slotAsset.slotId] = { type: 'audio', uri: assetStorageUrl };
            } else {
              // Use renderUrl when set (e.g. original MOV for endcard); otherwise fall back
              // to storageUrl which may be a proxy/preview URL for the same asset.
              const renderUri = slotAsset.renderUrl ?? assetStorageUrl;
              const trimConfig =
                slot.type === 'video'
                  ? getFixedDurationTrimConfig(slot.trim)
                  : null;
              const requiredDurationSeconds = trimConfig
                ? trimConfig.durationFrames / trimConfig.fps
                : null;
              const maxStartSeconds =
                trimConfig && typeof slotAsset.videoDurationSeconds === 'number' && requiredDurationSeconds !== null
                  ? Math.max(0, slotAsset.videoDurationSeconds - requiredDurationSeconds)
                  : null;
              const trimmedStartSeconds =
                trimConfig
                  ? Math.min(
                      Math.max(0, slotAsset.trimStartSeconds ?? 0),
                      maxStartSeconds ?? Number.POSITIVE_INFINITY,
                    )
                  : undefined;
              props[slotAsset.slotId] = {
                type: slot.type,
                uri: renderUri,
                fit_mode: slot.specs?.fit_mode ?? 'cover',
                crop_anchor: slotAsset.cropAnchor ?? slot.specs?.crop_anchor ?? 'center',
                ...(trimConfig
                  ? {
                      trim_start_seconds: trimmedStartSeconds,
                      trim_duration_frames: trimConfig.durationFrames,
                      trim_fps: trimConfig.fps,
                    }
                  : {}),
              };
            }
          } else {
            // Optional unfilled slot — pass null to Remotion
            props[slotAsset.slotId] = null;
          }
        }
      });

      // Include the loading video URL from the template config so the Remotion composition
      // can fetch it from Firebase Storage instead of relying on a local static file.
      if (selectedTemplate.loadingVideoUrl) {
        props['loadingVideoUrl'] = selectedTemplate.loadingVideoUrl;
      }
      if (dropCardAssetUrl) {
        props['dropCardAssetUrl'] = dropCardAssetUrl;
      }

      const jobName = getJobDisplayTitle(
        {
          templateId: selectedTemplate.id,
          market: selectedMarket,
          props,
        },
        selectedTemplate,
      );

      const jobId = await createJob({
        templateId: selectedTemplate.id,
        name: jobName,
        templateVersion: selectedTemplate.template_version,
        remotionComponentId: selectedTemplate.remotionCompositionId,
        props,
        market: selectedMarket,
        deliverableId: selectedTemplate.deliverable_id,
        format: selectedTemplate.format,
        outputFormat: selectedTemplate.output_format,
        effectGroupId: selectedEffectSelection?.group.id,
        effectGroupLabel: selectedEffectSelection?.group.label,
        effectOptionId: selectedEffectSelection?.option.id,
        effectOptionLabel: selectedEffectSelection?.option.label,
        effectOptionValue: selectedEffectSelection?.option.value,
        effectOptionAssetUrl: selectedEffectSelection?.option.assetUrl,
      });

      // Mark as submitted so that the dirty warning / blocker is suppressed.
      hasSubmittedRef.current = true;

      // Mark the resumed draft as consumed only after the new job exists.
      if (resumingDraftId) {
        try {
          await consumeDraft(resumingDraftId, jobId);
        } catch (err) {
          console.warn('[GeneratePage] failed to consume draft after Generate', err);
        }
        setResumingDraftId(null);
      }

      setCurrentJobId(jobId);
      // Navigate to Jobs and highlight the newly created job so the user can
      // monitor progress without having to manually locate it in the list.
      navigate(`/jobs?job=${jobId}`);
    } catch (error) {
      console.error('Failed to generate video:', error);
    }
  };

  const isSlotFilled = (slot: TemplateSlot, slotAsset: SlotAsset | undefined): boolean => {
    if (!slotAsset) return false;
    if (slot.type === 'text' || slot.type === 'select') {
      return !!slotAsset.textValue && slotAsset.textValue.trim().length > 0;
    }
    return slotAsset.file !== null || !!slotAsset.storageUrl;
  };

  const openingVideoCount = getOpeningVideoCount(
    slotAssets.find((sa) => sa.slotId === 'openingVideoCount')?.textValue,
  );

  const selectedEffectSelection = getResolvedEffectSelection(
    selectedTemplate?.effectGroups,
    slotAssets.find((sa) => sa.slotId === 'dropCardVariant')?.textValue ?? null,
    selectedMarket,
  );
  // Derive the current dropCardVariant value for the Dream Cat overlay and Remotion props.
  const dropCardVariant = selectedEffectSelection?.option.value
    ?? slotAssets.find((sa) => sa.slotId === 'dropCardVariant')?.textValue
    ?? null;
  const dropCardAssetUrl = selectedEffectSelection?.option.assetUrl ?? null;

  // Map of slotId → dynamic trim config for opening video slots (video1–video5).
  // These take precedence over static per-slot trim metadata from the template JSON.
  const openingSegmentMap = new Map(
    getOpeningSegments(openingVideoCount).map((seg) => [
      seg.slotId,
      { durationFrames: seg.durationFrames, fps: OPENING_FPS, loopPreview: false as const },
    ]),
  );

  const isRuntimeSlotVisible = (slot: TemplateSlot): boolean =>
    isSlotVisible(slot, openingVideoCount);

  const isRuntimeSlotRequired = (slot: TemplateSlot): boolean =>
    isSlotRequired(slot, openingVideoCount);

  // Audio slots never block render per spec.
  const blockingRequiredSlots = selectedTemplate?.slots.filter(
    (s) => isRuntimeSlotVisible(s) && isRuntimeSlotRequired(s) && s.type !== 'audio',
  ) ?? [];

  const missingRequiredCount = blockingRequiredSlots.filter(
    (slot) => !isSlotFilled(slot, slotAssets.find((sa) => sa.slotId === slot.slotId)),
  ).length;

  // Block render if any text slot exceeds its max_chars limit.
  const textSlotsOverLimit = selectedTemplate?.slots.filter((s) => {
    if (!isRuntimeSlotVisible(s) || s.type !== 'text' || !s.specs?.max_chars) return false;
    const sa = slotAssets.find((sa) => sa.slotId === s.slotId);
    const charCount = getVisibleCharCount(sa?.textValue ?? '', s.slotId);
    return charCount > s.specs.max_chars;
  }) ?? [];

  const trimValidationErrors = selectedTemplate?.slots.flatMap((slot) => {
    if (!isRuntimeSlotVisible(slot) || slot.type !== 'video') return [];
    // Opening video slots use dynamic durations; all others use static slot trim metadata.
    const trimConfig = openingSegmentMap.get(slot.slotId) ?? getFixedDurationTrimConfig(slot.trim);
    if (!trimConfig) return [];
    const slotAsset = slotAssets.find((sa) => sa.slotId === slot.slotId);
    if (!slotAsset || !isSlotFilled(slot, slotAsset)) return [];
    if (
      typeof slotAsset.videoDurationSeconds !== 'number' ||
      !Number.isFinite(slotAsset.videoDurationSeconds)
    ) {
      return [`${slot.label}: unable to read video duration`];
    }
    const requiredDurationSeconds = trimConfig.durationFrames / trimConfig.fps;
    if (slotAsset.videoDurationSeconds + TRIM_EPSILON < requiredDurationSeconds) {
      return [
        `${slot.label}: source video ${slotAsset.videoDurationSeconds.toFixed(2)} s is shorter than required ${requiredDurationSeconds.toFixed(2)} s`,
      ];
    }
    return [];
  }) ?? [];

  // Detect a template version mismatch: the live Firestore version differs from
  // the version that was locked when the operator opened this template.
  const templateVersionMismatch =
    !!lockedTemplateVersion &&
    !!selectedTemplate &&
    selectedTemplate.template_version !== lockedTemplateVersion;

  const canRender =
    missingRequiredCount === 0 &&
    textSlotsOverLimit.length === 0 &&
    trimValidationErrors.length === 0 &&
    !templateVersionMismatch;

  const isGenerating =
    jobStatus?.status === 'queued' ||
    jobStatus?.status === 'preprocessing' ||
    jobStatus?.status === 'rendering';

  const handleUploadAssets = async (files: File[]) => {
    console.log(`[GeneratePage] bulk upload start — ${files.length} file(s)`);
    let successCount = 0;
    // Upload files sequentially so that a single slow or failing upload cannot
    // stall the entire batch or leave the UI in an infinite loading state.
    for (const file of files) {
      try {
        await uploadAsset(file, 'manual');
        successCount++;
        console.log(`[GeneratePage] file done (${successCount}/${files.length}) — "${file.name}"`);
      } catch (error) {
        console.error(`[GeneratePage] file failed — "${file.name}"`, error);
      }
    }
    console.log(`[GeneratePage] bulk upload complete — ${successCount}/${files.length} succeeded`);
    setShowUploadModal(false);
  };

  // Left Panel Content - Asset Gallery
  const leftPanelContent = selectedTemplate && (
    <>
      <AssetGallery
        assets={assets}
        loading={assetsLoading}
        loadingMore={assetsLoadingMore}
        hasMore={assetsHasMore}
        totalCount={assetsTotalCount}
        onLoadMore={loadMoreAssets}
        onUploadClick={() => setShowUploadModal(true)}
      />
      <AssetUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadAssets}
      />
    </>
  );

  // Right Sidebar Content
  // selectedMediaSlot: image/video only — drives the "Your Asset" preview panel.
  const selectedMediaSlot = selectedTemplate?.slots.find(
    (s) => s.slotId === selectedSlotId && (s.type === 'image' || s.type === 'video'),
  );
  // selectedReferenceSlot: any slot type — drives the "Template Reference" preview panel.
  const selectedReferenceSlot = selectedTemplate?.slots.find(
    (s) => s.slotId === selectedSlotId,
  );
  const selectedSlotAsset = slotAssets.find((sa) => sa.slotId === selectedSlotId);
  const selectedSlotTrimConfig: FixedDurationTrimConfig | null =
    selectedMediaSlot?.type === 'video'
      ? (openingSegmentMap.get(selectedMediaSlot.slotId) ?? getFixedDurationTrimConfig(selectedMediaSlot.trim))
      : null;
  const selectedSlotIsOpeningVideo = !!selectedMediaSlot?.slotId && isOpeningVideoSlot(selectedMediaSlot.slotId);
  const shouldAutoPlaySelectedSlotPreview =
    selectedMediaSlot?.type === 'video' ||
    selectedMediaSlot?.slotId === 'endCardVideo' ||
    !!selectedSlotTrimConfig?.loopPreview ||
    selectedSlotIsOpeningVideo;
  const shouldLoopSelectedSlotPreview =
    selectedMediaSlot?.type === 'video' ||
    selectedMediaSlot?.slotId === 'endCardVideo' ||
    selectedSlotIsOpeningVideo;

  /**
   * Computes the template reference preview config for the currently selected slot.
   * For opening video slots (video1–video5) the window is derived dynamically from
   * getOpeningSegments so it always matches the live openingVideoCount selection.
   * For all other slots, static `referencePreview` metadata from the template JSON is used.
   * Returns null when no reference data is available (graceful degradation).
   */
  const selectedSlotReferencePreview: SlotReferencePreview | null = useMemo(() => {
    if (!selectedReferenceSlot || !selectedTemplate?.previewVideoUrl) return null;
    // Opening video slots: compute dynamically from getOpeningSegments.
    if (isOpeningVideoSlot(selectedReferenceSlot.slotId)) {
      const seg = getOpeningSegments(openingVideoCount).find(
        (s) => s.slotId === selectedReferenceSlot.slotId,
      );
      if (seg) {
        return {
          source: 'templatePreview' as const,
          startTimeSeconds: seg.startFrame / OPENING_FPS,
          durationSeconds: seg.durationFrames / OPENING_FPS,
          label: `Opening slot — appears at ${(seg.startFrame / OPENING_FPS).toFixed(1)}s–${((seg.endFrame + 1) / OPENING_FPS).toFixed(1)}s`,
        };
      }
    }
    // All other slots: use static metadata from the template JSON.
    return selectedReferenceSlot.referencePreview ?? null;
  }, [selectedReferenceSlot, selectedTemplate?.previewVideoUrl, openingVideoCount]);

  const rightSidebarContent = selectedTemplate && (
    <div className="space-y-4">
      {/* Template Preview */}
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Template Preview
        </h3>
        {selectedTemplate.previewVideoUrl && !previewVideoError ? (
          <video
            src={selectedTemplate.previewVideoUrl}
            autoPlay
            loop
            muted
            playsInline
            aria-label={`${selectedTemplate.name} preview video`}
            onError={() => setPreviewVideoError(true)}
            className="w-full rounded-md border border-border-subtle"
          />
        ) : selectedTemplate.previewImageUrl ? (
          <img
            src={selectedTemplate.previewImageUrl}
            alt={`${selectedTemplate.name} preview`}
            className="w-full rounded-md border border-border-subtle"
          />
        ) : (
          <div className="w-full aspect-[9/16] bg-bg-tertiary rounded-md border border-border-subtle flex items-center justify-center">
            <div className="text-center p-4">
              <Video className="w-12 h-12 text-text-tertiary mx-auto mb-2" />
              <p className="text-xs text-text-tertiary">
                {selectedTemplate.name}
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                {selectedTemplate.duration}s • {selectedTemplate.slots.filter((s) => s.visible !== false).length} slots
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Slot Preview — shown for image/video slots */}
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Slot Preview
        </h3>
        {/* Your Asset preview — only for image/video slots with an uploaded asset */}
        {selectedMediaSlot && (selectedSlotAsset?.file || selectedSlotAsset?.storageUrl) ? (
            selectedMediaSlot.type === 'video' ? (
              <VideoSlotPreview
                key={selectedSlotId}
                file={selectedSlotAsset.file}
                url={selectedSlotAsset.storageUrl}
                cropAnchor={selectedSlotAsset.cropAnchor ?? selectedMediaSlot.specs?.crop_anchor}
                slotAspectRatio={selectedMediaSlot.specs?.aspect_ratio}
                trimStartSeconds={selectedSlotTrimConfig ? selectedSlotAsset.trimStartSeconds ?? 0 : undefined}
                trimDurationSeconds={
                  selectedSlotTrimConfig
                    ? selectedSlotTrimConfig.durationFrames / selectedSlotTrimConfig.fps
                    : null
                }
                // Loop the trim window for opening video slots so the sidebar
                // preview shows exactly the template segment, not the whole asset.
                loopSelectedSegment={!!selectedSlotTrimConfig?.loopPreview || selectedSlotIsOpeningVideo}
                autoPlay={shouldAutoPlaySelectedSlotPreview}
                loop={shouldLoopSelectedSlotPreview}
                templateId={selectedTemplateId}
                slotId={selectedSlotId}
                market={selectedMarket}
                dropCardVariant={dropCardVariant}
                dropCardAssetUrl={dropCardAssetUrl}
                openingVideoCount={openingVideoCount}
              />
            ) : (
              <ImageSlotPreview
                key={selectedSlotId}
                file={selectedSlotAsset.file}
                url={selectedSlotAsset.storageUrl}
                fitMode={selectedMediaSlot.specs?.fit_mode ?? 'cover'}
                cropAnchor={selectedSlotAsset.cropAnchor ?? selectedMediaSlot.specs?.crop_anchor}
                slotAspectRatio={selectedMediaSlot.specs?.aspect_ratio}
              />
            )
          ) : !selectedReferenceSlot ? (
            <div className="w-full bg-bg-tertiary rounded-md border border-border-subtle flex items-center justify-center py-8">
              <p className="text-xs text-text-tertiary text-center px-4">
                Select a slot to preview its position in the template
              </p>
            </div>
          ) : selectedMediaSlot && !selectedSlotAsset?.file && !selectedSlotAsset?.storageUrl ? (
            <div className="w-full bg-bg-tertiary rounded-md border border-border-subtle flex items-center justify-center py-6">
              <p className="text-xs text-text-tertiary text-center px-4">
                Upload an asset to see the crop preview
              </p>
            </div>
          ) : null}

        {/* Template Reference preview — shown whenever the selected slot has reference
            metadata (or is an opening video slot with a dynamic reference), regardless
            of whether a media asset has been uploaded. Requires previewVideoUrl. */}
        {selectedSlotReferencePreview && selectedTemplate.previewVideoUrl && (
          <TemplateReferencePreview
            key={`ref-${selectedSlotId}`}
            previewVideoUrl={selectedTemplate.previewVideoUrl}
            startTimeSeconds={selectedSlotReferencePreview.startTimeSeconds}
            durationSeconds={selectedSlotReferencePreview.durationSeconds}
            label={selectedSlotReferencePreview.label}
          />
        )}
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Template Info
        </h3>
        <div className="space-y-2 text-xs text-text-secondary">
          <div className="flex justify-between">
            <span>Template:</span>
            <span className="text-text-primary font-medium">
              {selectedTemplate.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Version:</span>
            <span className="text-text-primary font-medium">
              v{selectedTemplate.version}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Duration:</span>
            <span className="text-text-primary font-medium">
              {selectedTemplate.duration}s
            </span>
          </div>
          <div className="flex justify-between">
            <span>Slots:</span>
            <span className="text-text-primary font-medium">
              {selectedTemplate.slots.filter((s) => s.visible !== false).length}
            </span>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-3">
          Asset Guidelines
        </h3>
        <ul className="space-y-2 text-xs text-text-secondary">
          <li>• Images: JPEG format, max 100MB</li>
          <li>• Videos: MPEG format, max 100MB</li>
          <li>• Ensure high quality assets</li>
          <li>• Follow template slot requirements</li>
        </ul>
      </Card>
    </div>
  );

  // Determine which header action button to show.
  const headerActions = () => {
    if (inNewRun && !currentJobId) {
      // Inside a new run — offer "Back to Runs"
      return !isGenerating ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={handleBackToRunList}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Runs
        </Button>
      ) : null;
    }
    if (selectedTemplate && !inNewRun && !currentJobId) {
      // Run list view — offer "Back to Template Selection"
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={handleReset}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Selection
        </Button>
      );
    }
    return null;
  };

  return (
    <>
      <DraftWarningModal
        isOpen={showDraftModal}
        onSaveDraft={handleModalSaveDraft}
        onDiscard={handleModalDiscard}
        onCancel={handleModalCancel}
        saving={savingDraft || uploadingForDraft}
      />
    <AppLayout
      title="Generate Video"
      subtitle="Create a new video from a template"
      leftPanel={leftPanelContent}
      leftPanelVisible={!!selectedTemplate && inNewRun}
      rightSidebar={rightSidebarContent}
      rightSidebarVisible={!!selectedTemplate && inNewRun}
      actions={headerActions()}
    >
      <div className={`${inNewRun ? 'w-full' : 'max-w-4xl mx-auto'} space-y-6`}>
        {/* Market Selection — always visible, filters runs */}
        <Card>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-text-primary">
              Market:
            </label>
            <Select
              value={selectedMarket}
              onChange={(m) => {
                const doMarketChange = () => {
                  setSelectedMarket(m);
                  setCurrentJobId(null);
                  setResumingDraftId(null);
                  hasSubmittedRef.current = false;
                  setInNewRun(false);
                  if (!dropCardVariantIsManual.current) {
                    if (selectedTemplate?.effectGroups?.length) {
                      const defaultSelection = getDefaultEffectSelection(selectedTemplate.effectGroups, m);
                      if (defaultSelection) {
                        setSlotAssets((prev) =>
                          prev.map((sa) => sa.slotId === 'dropCardVariant' ? { ...sa, textValue: defaultSelection.option.id } : sa)
                        );
                      }
                    } else {
                      const variant = getMarketDefaultVariant(m);
                      setSlotAssets((prev) =>
                        prev.map((sa) => sa.slotId === 'dropCardVariant' ? { ...sa, textValue: variant } : sa)
                      );
                    }
                  }
                };
                guardedAction(doMarketChange);
              }}
              options={[...MARKETS]}
              disabled={isGenerating}
              className="w-48"
            />
          </div>
        </Card>

        {/* ── Step 1: Template Selection ── */}
        {!selectedTemplate && (
          <TemplateSelector
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={setSelectedTemplateId}
            disabled={templatesLoading || isGenerating}
            selectedMarket={selectedMarket}
          />
        )}

        {/* ── Step 2: Run List (market-filtered via useRuns) ── */}
        {selectedTemplate && !inNewRun && !currentJobId && (
          <RunList
            runs={runs}
            loading={runsLoading}
            template={selectedTemplate}
            onNewRun={() => setInNewRun(true)}
            onResumeDraft={handleResumeDraft}
            onDeleteDraft={handleDeleteDraft}
          />
        )}

        {/* ── Step 3: New Run — slot filling ── */}
        {selectedTemplate && inNewRun && !currentJobId && (
          <>
            {/* Template version mismatch warning */}
            {templateVersionMismatch && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/40 rounded-md">
                <p className="text-sm text-amber-600 font-semibold mb-1">
                  Template updated — version mismatch
                </p>
                <p className="text-xs text-amber-700">
                  This template was updated to{' '}
                  <span className="font-medium">v{selectedTemplate.template_version}</span> after
                  you opened it (you have{' '}
                  <span className="font-medium">v{lockedTemplateVersion}</span>). Please go back and
                  re-select the template to continue.
                </p>
              </div>
            )}

            <SceneGroupedSlots
              slots={selectedTemplate.slots}
              slotAssets={slotAssets}
              templateId={selectedTemplateId}
              market={selectedMarket}
              openingVideoCount={openingVideoCount}
              isSlotVisible={isRuntimeSlotVisible}
              isSlotRequired={isRuntimeSlotRequired}
              onAssetAdded={handleAssetAdded}
              onAssetRemoved={handleAssetRemoved}
              onTextChanged={handleTextChanged}
              onCropAnchorChanged={handleCropAnchorChanged}
              onTrimStartChanged={handleTrimStartChanged}
              onVideoDurationDetected={handleVideoDurationDetected}
              onGalleryAssetAdded={handleGalleryAssetAdded}
              disabled={uploading || creating}
              selectedSlotId={selectedSlotId}
              onSlotSelected={setSelectedSlotId}
              endCardOptions={selectedTemplate.endCardOptions}
              effectGroups={selectedTemplate.effectGroups}
            />

            {/* Render + Save Draft buttons */}
            <div className="flex flex-col items-center gap-2 pt-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleGenerateVideo}
                  disabled={!canRender || uploading || creating}
                  isLoading={uploading || creating}
                  className="flex items-center gap-2 min-w-[200px]"
                >
                  <Video className="w-5 h-5" />
                  {uploading
                    ? 'Uploading Assets...'
                    : creating
                    ? 'Creating Job...'
                    : templateVersionMismatch
                    ? 'Template version mismatch'
                    : textSlotsOverLimit.length > 0
                    ? `${textSlotsOverLimit.length} text slot${textSlotsOverLimit.length === 1 ? '' : 's'} over limit`
                    : trimValidationErrors.length > 0
                    ? `${trimValidationErrors.length} trim validation issue${trimValidationErrors.length === 1 ? '' : 's'}`
                    : missingRequiredCount > 0
                    ? `${missingRequiredCount} required slot${missingRequiredCount === 1 ? '' : 's'} missing`
                    : 'Render'}
                </Button>
                {isDirty && (
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={handleSaveDraft}
                    disabled={savingDraft || uploadingForDraft || uploading || creating}
                    isLoading={savingDraft || uploadingForDraft}
                    className="flex items-center gap-2"
                  >
                    <FileEdit className="w-5 h-5" />
                    {uploadingForDraft
                      ? 'Uploading…'
                      : savingDraft
                      ? 'Saving…'
                      : resumingDraftId
                      ? 'Update Draft'
                      : 'Save Draft'}
                  </Button>
                )}
              </div>
              {createJobError && (
                <p className="text-xs text-accent-red text-center max-w-sm">
                  {createJobError}
                </p>
              )}
              {draftSaveError && (
                <p className="text-xs text-accent-red text-center max-w-sm">
                  Draft save failed: {draftSaveError}
                </p>
              )}
              {trimValidationErrors.length > 0 && (
                <div className="max-w-xl rounded-md border border-accent-red/50 bg-accent-red/5 px-3 py-2">
                  <p className="text-xs font-semibold text-accent-red mb-1">Trim validation errors</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {trimValidationErrors.map((error) => (
                      <li key={error} className="text-xs text-accent-red">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Step 4: Generation Progress ── */}
        {currentJobId && (
          <GenerationProgress
            jobStatus={jobStatus}
            onDownload={() => {
              const url = jobStatus?.outputVideoPublicUrl;
              if (!url) return;
              setDownloadError(null);
              const filename = selectedTemplate
                ? `${selectedTemplate.deliverable_id}_${selectedTemplate.id}_rendered.mp4`
                : 'video.mp4';
              // Use a direct anchor navigation instead of fetch() to avoid
              // cross-origin CORS errors when the output URL is on a different
              // host (e.g. 127.0.0.1:8090). The browser handles the
              // download/navigation natively without a preflight request.
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              a.style.display = 'none';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            downloadError={downloadError}
            onReset={handleBackToRunList}
          />
        )}

        {/* Empty State */}
        {!selectedTemplate && templates.length === 0 && !templatesLoading && (
          <Card className="text-center py-12">
            <div className="max-w-md mx-auto">
              <Video className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
              {templatesError ? (
                <>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Failed to Load Templates
                  </h3>
                  <p className="text-sm text-text-secondary">
                    {templatesError}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    No Templates Available
                  </h3>
                  <p className="text-sm text-text-secondary">
                    There are no active templates available. Please contact an administrator to add templates.
                  </p>
                </>
              )}
            </div>
          </Card>
        )}
      </div>
    </AppLayout>
    </>
  );
}

export default GeneratePage;
